import React, { useMemo, useCallback } from "react";
// @ts-expect-error - this will be resolved at build time
import { Box, Text } from "@xsolla/xui-primitives";
import { useResolvedTheme } from "@xsolla/xui-core";
import {
  addDays,
  eachDayOfInterval,
  endOfDay,
  endOfMonth,
  endOfWeek,
  format,
  isAfter,
  isBefore,
  isSameDay,
  isSameMonth,
  isToday,
  isWithinInterval,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import * as locales from "date-fns/locale";
import type { CalendarGridProps } from "./types";

/** Width of a single day cell. */
const DAY_CELL_WIDTH = 40;
/**
 * Natural width of the 7-column grid. The header and week rows are pinned to
 * this so the cells sit flush against each other: a range highlight has to read
 * as one continuous band, which it cannot do if the row distributes slack
 * between the cells. See FEP-834.
 */
const GRID_WIDTH = DAY_CELL_WIDTH * 7;

export const CalendarGrid: React.FC<CalendarGridProps> = ({
  currentMonth,
  locale = "enUS",
  firstDayOfWeek = 0,
  selectsRange = false,
  minDate,
  maxDate,
  startDate,
  endDate,
  selectedDate,
  selectingRange,
  onDayPress,
  testID,
  themeMode,
  themeProductContext,
}) => {
  const { theme } = useResolvedTheme({ themeMode, themeProductContext });
  const localeObj = (locales as any)[locale] || locales.enUS;

  const monthStart = startOfMonth(currentMonth);
  const monthEnd = endOfMonth(currentMonth);
  const weekStartsOn = (firstDayOfWeek ?? 0) as any;
  const calendarStart = startOfWeek(monthStart, { weekStartsOn });
  const calendarEnd = endOfWeek(monthEnd, { weekStartsOn });

  const days = eachDayOfInterval({ start: calendarStart, end: calendarEnd });

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }, (_, i) => {
      const day = addDays(calendarStart, i);
      return format(day, "EEEEEE", { locale: localeObj });
    });
  }, [calendarStart, localeObj]);

  const weeks = useMemo(() => {
    const weekArray: Date[][] = [];
    for (let i = 0; i < days.length; i += 7) {
      weekArray.push(days.slice(i, i + 7));
    }
    return weekArray;
  }, [days]);

  const isDateDisabled = useCallback(
    (date: Date) => {
      if (minDate && isBefore(date, startOfDay(minDate))) return true;
      if (maxDate && isAfter(date, endOfDay(maxDate))) return true;
      return false;
    },
    [minDate, maxDate]
  );

  const isDateInRange = useCallback(
    (date: Date) => {
      if (!selectsRange || !startDate) return false;
      const rangeEnd = endDate || selectingRange;
      if (!rangeEnd) return false;
      const start = startDate < rangeEnd ? startDate : rangeEnd;
      const end = startDate < rangeEnd ? rangeEnd : startDate;
      return isWithinInterval(date, {
        start: startOfDay(start),
        end: endOfDay(end),
      });
    },
    [selectsRange, startDate, endDate, selectingRange]
  );

  return (
    <Box testID={testID}>
      <Box
        flexDirection="row"
        justifyContent="flex-start"
        width={GRID_WIDTH}
        marginBottom={8}
      >
        {weekDays.map((day, i) => (
          <Box key={i} width={DAY_CELL_WIDTH} alignItems="center">
            <Text
              fontSize={12}
              fontWeight="600"
              color={theme.colors.content.tertiary}
            >
              {day.toUpperCase()}
            </Text>
          </Box>
        ))}
      </Box>

      <Box gap={2}>
        {weeks.map((week, i) => (
          <Box
            key={i}
            flexDirection="row"
            justifyContent="flex-start"
            width={GRID_WIDTH}
          >
            {week.map((day, j) => {
              const isOutsideMonth = !isSameMonth(day, currentMonth);
              const isSelected =
                (!selectsRange &&
                  selectedDate &&
                  isSameDay(day, selectedDate)) ||
                (selectsRange &&
                  startDate &&
                  endDate &&
                  isSameDay(day, startDate) &&
                  isSameDay(day, endDate));
              const isRangeStart =
                selectsRange && startDate && isSameDay(day, startDate);
              const isRangeEnd =
                selectsRange && endDate && isSameDay(day, endDate);
              const inRange = isDateInRange(day);
              const today = isToday(day);
              const disabled = isDateDisabled(day);

              let bgColor = "transparent";
              let textColor = theme.colors.content.primary;

              if (isSelected || isRangeStart || isRangeEnd) {
                bgColor = theme.colors.background.brand.primary;
                textColor = theme.colors.content.on.brand;
              } else if (inRange) {
                bgColor = theme.colors.overlay.brand;
              } else if (today) {
                bgColor = theme.colors.overlay.mono;
              }

              if (isOutsideMonth || disabled) {
                textColor = theme.colors.content.tertiary;
              }

              return (
                <Box
                  key={j}
                  width={DAY_CELL_WIDTH}
                  height={32}
                  alignItems="center"
                  justifyContent="center"
                  borderTopLeftRadius={isSelected || isRangeStart ? 4 : 0}
                  borderTopRightRadius={isSelected || isRangeEnd ? 4 : 0}
                  borderBottomLeftRadius={isSelected || isRangeStart ? 4 : 0}
                  borderBottomRightRadius={isSelected || isRangeEnd ? 4 : 0}
                  backgroundColor={bgColor}
                  cursor={disabled || isOutsideMonth ? "default" : "pointer"}
                  onPress={() =>
                    !disabled && !isOutsideMonth && onDayPress?.(day)
                  }
                  hoverStyle={
                    !disabled &&
                    !isOutsideMonth &&
                    !isSelected &&
                    !isRangeStart &&
                    !isRangeEnd
                      ? { backgroundColor: theme.colors.overlay.mono }
                      : undefined
                  }
                >
                  <Text
                    color={textColor}
                    fontSize={14}
                    fontWeight={
                      isSelected || isRangeStart || isRangeEnd ? "600" : "400"
                    }
                  >
                    {format(day, "d")}
                  </Text>
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>
    </Box>
  );
};
